/**
 * Legacy n8n payload parity (architecture.md §4 + "Response Normalization").
 *
 * The old stack answered in two unstable shapes that the SPA had to flatten in
 * `normalizeN8nPayload()`:
 *   - success: `{ success: true, ...data }`  (sometimes wrapped `{ response: {...} }`)
 *   - error:   `{ success: false, message }`  (and the variant `{ ..., error }`)
 *     e.g. the router's "session invalid" node returned
 *     `{ success:false, message:'unauthorized' }`
 *     (docs/source-n8n/raw/eer-cmd-router-backup.json).
 *
 * The new API never emits these shapes (it is clean REST). This module exists
 * so the migration keeps PARITY: anything that still has to read a legacy
 * payload (data import, a not-yet-rewritten edge) converts it deterministically
 * into the unified `ApiErrorBody`, and the legacy error message is preserved —
 * exactly the guarantee `normalizeN8nPayload()` gave the front-end.
 *
 * `details: undefined` is omitted from the produced body, keeping it byte-clean.
 */
import { z } from 'zod';
import { ApiError } from './errors.js';
/**
 * The legacy error envelope. `message` is the canonical field; `error` is the
 * older alias still present in a few n8n code-nodes. At least one must be a
 * non-empty string — that is the message we must not lose.
 */
export const legacyErrorSchema = z
    .object({
    success: z.literal(false),
    message: z.string().min(1).optional(),
    error: z.string().min(1).optional(),
})
    .refine((v) => Boolean(v.message ?? v.error), {
    message: 'legacy error payload must carry a message or error string',
});
/** Legacy success envelope: `{ success:true, ...data }`. */
export const legacySuccessSchema = z.object({ success: z.literal(true) }).passthrough();
/**
 * Map a legacy error message to a unified `ApiErrorCode`. The old payloads
 * carried no code, so we infer from the few well-known message strings the
 * router used (e.g. 'unauthorized' → UNAUTHENTICATED) and fall back to
 * INTERNAL. This is best-effort code inference; the message is always exact.
 */
function inferCode(message) {
    const m = message.trim().toLowerCase();
    if (m === 'unauthorized' || m.includes('session'))
        return 'UNAUTHENTICATED';
    // 'пароль' stays in the list: legacy n8n payloads still carry the Russian
    // message, and this maps THEIR text to a code (the v2 copy is English — KS2-10).
    if (m.includes('password') || m.includes('пароль') || m.includes('email'))
        return 'UNAUTHENTICATED';
    if (m.includes('forbidden') || m.includes('access'))
        return 'FORBIDDEN';
    if (m.includes('not found') || m.includes('missing'))
        return 'NOT_FOUND';
    return 'INTERNAL';
}
/**
 * Is `value` a legacy error payload (`{ success:false, message|error }`)?
 * Used by the web client / import path to decide whether to normalize.
 */
export function isLegacyErrorPayload(value) {
    return legacyErrorSchema.safeParse(value).success;
}
/**
 * Convert a legacy `{ success:false, message }` payload into the unified
 * `ApiErrorBody`, preserving the message (parity with `normalizeN8nPayload`).
 * Throws (zod) if the input is not a recognizable legacy error.
 */
export function normalizeLegacyError(value) {
    const parsed = legacyErrorSchema.parse(value);
    const message = (parsed.message ?? parsed.error);
    return new ApiError(inferCode(message), message).toBody();
}
/**
 * Unwrap a legacy success payload into the bare data the new API would return.
 * This is the success half of the old `normalizeN8nPayload()` and handles every
 * shape the n8n webhook could answer with, collapsing them to one form:
 *   - `[{ response: {...} }]` / `[{...}]` → the first array element, recursively
 *     unwrapped (the n8n "Respond to Webhook" node emitted an array of items);
 *   - `{ response: {...} }` → the inner object (the classic `{response}` wrapper);
 *   - `{ success:true, ...rest }` → `rest` (the `success` flag stripped);
 *   - a direct object → returned unchanged.
 * Idempotent: calling it on already-bare data returns that data.
 */
export function unwrapLegacySuccess(value) {
    // `[{response}]` — the webhook wrapped a single payload in a one-item array.
    if (Array.isArray(value)) {
        if (value.length === 1)
            return unwrapLegacySuccess(value[0]);
        return value;
    }
    if (value && typeof value === 'object') {
        const obj = value;
        if ('response' in obj)
            return obj.response;
        if (obj.success === true) {
            const { success: _success, ...rest } = obj;
            return rest;
        }
    }
    return value;
}
/**
 * Collapse ANY parsed n8n/backend payload into the unified `{ success, error?,
 * ...data }` envelope the live SPA expects (the canonical `normalizeN8nPayload`,
 * acceptance E10-F01-T07). This is the JSON-body branch — the raw `Response`
 * status/non-JSON handling lives in the web client's `responseToLegacyEnvelope`.
 *
 * Shapes handled (all unwrapped via {@link unwrapLegacySuccess} first, so a
 * `[{response}]` array, a `{response}` wrapper and a direct object behave
 * identically):
 *   - legacy error `{ success:false, message|error }` → `{ success:false,
 *     error: <message>, message: <message> }` (the `error` string the UI keys on
 *     plus the human message, both preserved);
 *   - legacy success `{ success:true, ...data }` / `{ response:{...} }` /
 *     a direct object → `{ success:true, ...data }`;
 *   - a non-object (string/number/null) → `{ success:true, data: <value> }` so
 *     the envelope shape is always uniform.
 * Idempotent: an already-normalized `{ success, ... }` round-trips unchanged.
 */
export function normalizeN8nPayload(value) {
    // Error half: detect BEFORE unwrapping (unwrap strips the `success` flag).
    if (isLegacyErrorPayload(value)) {
        const message = (value.message ?? value.error);
        return { success: false, error: message, message };
    }
    const data = unwrapLegacySuccess(value);
    // Already-normalized envelope (idempotency) — preserve its flag and fields.
    if (data && typeof data === 'object' && !Array.isArray(data) && 'success' in data) {
        const obj = data;
        return { ...obj, success: obj.success === true };
    }
    // A bare object → spread its fields next to a success flag.
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        return { success: true, ...data };
    }
    // A non-object success body (array/string/number/null) → carry it as `data`.
    return { success: true, data };
}
//# sourceMappingURL=legacy.js.map